// <div data-controller="coupons--form">
//
// </div>
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import * as helpers from "@/utils/helpers";

// https://stimulus.hotwired.dev/reference
export default class extends Controller {
  static targets = ["discountType", "discountValue", "percentageWarning", "discountPreview", "monthlyPreview", "yearlyPreview", "validFrom", "validUntil"];
  static classes = [ "" ];
  static outlets = [];
  static values = { monthlyFee: Number, yearlyFee: Number };

  initialize() {

  }

  connect() {
    this.updateValidation(false) // Don't auto-correct on initial load
    this.updatePreview()
    this.validateDates()
  }

  disconnect() {

  }

  updateValidation(autoCorrect = true) {
    const discountType = this.discountTypeTarget.value
    
    if (discountType === 'percentage') {
      // 정률: 1~100
      this.discountValueTarget.min = 1
      this.discountValueTarget.max = 100
      this.discountValueTarget.step = 1
      this.discountValueTarget.placeholder = "1~100"
      
      // Only auto-correct on blur, not while typing
      if (autoCorrect && this.discountValueTarget.value) {
        const value = parseFloat(this.discountValueTarget.value)
        if (value > 100) {
          this.discountValueTarget.value = 100
        }
      }
      
      this.discountValueTarget.setCustomValidity('')
      if (this.discountValueTarget.value) {
        const value = parseFloat(this.discountValueTarget.value)
        if (value < 1 || value > 100) {
          this.discountValueTarget.setCustomValidity('정률 할인은 1~100 사이의 값이어야 합니다.')
        }
      }
    } else if (discountType === 'fixed_amount') {
      // 정액: 100원 단위, 100~월간 멤버십 금액
      const maxAmount = this.monthlyFeeValue
      this.discountValueTarget.min = 100
      this.discountValueTarget.max = maxAmount
      this.discountValueTarget.step = 100
      this.discountValueTarget.placeholder = `100~${helpers.numberWithDelimiter(maxAmount)} (100원 단위)`
      
      // Only auto-correct on blur, not while typing
      if (autoCorrect && this.discountValueTarget.value) {
        const value = parseFloat(this.discountValueTarget.value)
        
        // Round to nearest 100 only on blur
        if (value % 100 !== 0) {
          this.discountValueTarget.value = Math.round(value / 100) * 100
        }
        
        if (value > maxAmount) {
          this.discountValueTarget.value = Math.floor(maxAmount / 100) * 100
        }
      }
      
      this.discountValueTarget.setCustomValidity('')
      if (this.discountValueTarget.value) {
        const value = parseFloat(this.discountValueTarget.value)
        // Allow typing intermediate values, only validate the final value
        if (autoCorrect) {
          if (value < 100 || value > maxAmount) {
            this.discountValueTarget.setCustomValidity(`정액 할인은 100~${helpers.numberWithDelimiter(maxAmount)}원 사이의 값이어야 합니다.`)
          } else if (value % 100 !== 0) {
            this.discountValueTarget.setCustomValidity('정액 할인은 100원 단위여야 합니다.')
          }
        }
      }
    } else {
      // No discount type selected
      this.discountValueTarget.removeAttribute('max')
      this.discountValueTarget.min = 1
      this.discountValueTarget.step = 1
      this.discountValueTarget.placeholder = ""
    }
    
    // Only report validity on blur/submit, not while typing
    if (autoCorrect) {
      this.discountValueTarget.reportValidity()
    }
  }

  discountTypeChanged() {
    // Only validate if there's a value
    if (this.discountValueTarget.value) {
      this.updateValidation()
    } else {
      // Just update the placeholder and constraints without validation
      this.updateValidation(false)
    }
    this.updatePreview()
  }

  discountValueChanged() {
    // Don't validate while typing, only update preview
    this.updatePreview()
  }
  
  discountValueBlurred() {
    // Validate when user finishes typing (on blur), but not if empty
    if (this.discountValueTarget.value) {
      this.updateValidation()
    }
    this.updatePreview()
  }

  updatePreview() {
    const discountType = this.discountTypeTarget.value
    const discountValue = parseFloat(this.discountValueTarget.value) || 0

    if (!discountType || discountValue <= 0) {
      this.hideElements()
      return
    }

    if (discountType === 'percentage') {
      this.showPercentageDiscount(discountValue)
    } else if (discountType === 'fixed_amount') {
      this.showFixedAmountDiscount(discountValue)
    } else {
      this.hideElements()
    }
  }

  showPercentageDiscount(discountValue) {
    this.percentageWarningTarget.style.display = 'block'

    const monthlyDiscount = Math.round(this.monthlyFeeValue * discountValue / 100)
    const yearlyDiscount = Math.round(this.yearlyFeeValue * discountValue / 100)
    const discountedMonthly = this.monthlyFeeValue - monthlyDiscount
    const discountedYearly = this.yearlyFeeValue - yearlyDiscount

    this.monthlyPreviewTarget.innerHTML = `
      <strong>월간 멤버십:</strong> 
      <span style="text-decoration: line-through;">${this.membershipKrw(this.monthlyFeeValue)}</span> 
      → ${this.membershipKrw(discountedMonthly)} 
      <span class="text-red-600">(${this.membershipKrw(monthlyDiscount)} 할인)</span>
    `

    this.yearlyPreviewTarget.innerHTML = `
      <strong>연간 멤버십:</strong> 
      <span style="text-decoration: line-through;">${this.membershipKrw(this.yearlyFeeValue)}</span> 
      → ${this.membershipKrw(discountedYearly)} 
      <span class="text-red-600">(${this.membershipKrw(yearlyDiscount)} 할인)</span>
    `

    this.discountPreviewTarget.style.display = 'block'
  }

  showFixedAmountDiscount(discountValue) {
    this.percentageWarningTarget.style.display = 'none'

    const monthlyDiscount = Math.min(discountValue, this.monthlyFeeValue)
    const yearlyDiscount = Math.min(discountValue, this.yearlyFeeValue)
    const discountedMonthly = this.monthlyFeeValue - monthlyDiscount
    const discountedYearly = this.yearlyFeeValue - yearlyDiscount

    this.monthlyPreviewTarget.innerHTML = `
      <strong>월간 멤버십:</strong> 
      <span style="text-decoration: line-through;">${this.membershipKrw(this.monthlyFeeValue)}</span> 
      → ${this.membershipKrw(discountedMonthly)} 
      <span class="text-red-600">(${this.membershipKrw(monthlyDiscount)} 할인)</span>
    `

    this.yearlyPreviewTarget.innerHTML = `
      <strong>연간 멤버십:</strong> 
      <span style="text-decoration: line-through;">${this.membershipKrw(this.yearlyFeeValue)}</span> 
      → ${this.membershipKrw(discountedYearly)} 
      <span class="text-red-600">(${this.membershipKrw(yearlyDiscount)} 할인)</span>
    `

    this.discountPreviewTarget.style.display = 'block'
  }

  hideElements() {
    this.percentageWarningTarget.style.display = 'none'
    this.discountPreviewTarget.style.display = 'none'
  }

  membershipKrw(price) {
    if (!price || price === 0) return '0원'
    
    const manwon = Math.floor(price / 10000)
    const chunwon = price % 10000
    let result = []
    
    if (manwon > 0) result.push(`${manwon}만`)
    if (chunwon > 0) result.push(helpers.numberWithDelimiter(chunwon))
    
    return result.join(' ') + '원'
  }
  
  validateDates() {
    if (!this.hasValidFromTarget || !this.hasValidUntilTarget) return
    
    const validFrom = this.validFromTarget.value
    const validUntil = this.validUntilTarget.value
    
    if (validFrom && validUntil) {
      const fromDate = new Date(validFrom)
      const untilDate = new Date(validUntil)
      const now = new Date()
      
      // Clear any previous custom validity
      this.validUntilTarget.setCustomValidity('')
      
      // Check if end date is before or equal to start date
      if (untilDate <= fromDate) {
        this.validUntilTarget.setCustomValidity('종료 일시는 시작 일시보다 이후여야 합니다.')
        this.validUntilTarget.reportValidity()
      }
      // Check if end date is before current time
      else if (untilDate <= now) {
        this.validUntilTarget.setCustomValidity('종료 일시는 현재 시간보다 이후여야 합니다.')
        this.validUntilTarget.reportValidity()
      }
    }
  }
  
  dateChanged() {
    this.validateDates()
  }
}